import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCirclePlay, faVideo } from '@fortawesome/free-solid-svg-icons';

interface YouTubeVideoPopupProps {
  videoUrl: string;
  linkText?: string;
  variant?: 'text' | 'button';
  style?: React.CSSProperties;
  className?: string;
}

/**
 * YouTube Video Popup Component
 * 
 * A reusable component that displays a clickable link/button which opens
 * a YouTube video in a closable modal overlay.
 */
const YouTubeVideoPopup: React.FC<YouTubeVideoPopupProps> = ({ 
  videoUrl,
  linkText = "Watch Tutorial Video",
  variant = 'text',
  style = {},
  className = ''
}) => {
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [hoveredLink, setHoveredLink] = useState(false);

  // Extract YouTube video ID from various URL formats
  const getYouTubeVideoId = (url: string): string | null => {
    if (!url) return null;
    
    // Handle youtu.be short URLs
    const shortUrlMatch = url.match(/youtu\.be\/([a-zA-Z0-9_-]+)/);
    if (shortUrlMatch) return shortUrlMatch[1];
    
    // Handle youtube.com URLs
    const longUrlMatch = url.match(/[?&]v=([a-zA-Z0-9_-]+)/);
    if (longUrlMatch) return longUrlMatch[1];
    
    // Handle embed URLs
    const embedMatch = url.match(/embed\/([a-zA-Z0-9_-]+)/);
    if (embedMatch) return embedMatch[1];
    
    return null;
  };

  const videoId = getYouTubeVideoId(videoUrl);

  // Link/Button styles based on variant (matching ReportInaccuraciesButton style)
  const getLinkStyle = (): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      transition: 'color 200ms',
      ...style
    };

    if (variant === 'button') {
      return {
        ...baseStyle,
        padding: '10px 20px',
        background: 'rgba(59, 130, 246, 0.2)',
        border: '1px solid rgba(59, 130, 246, 0.5)',
        borderRadius: '8px',
        color: hoveredLink ? '#93c5fd' : '#60a5fa',
        fontSize: '14px',
        fontWeight: 600,
        gap: '8px'
      };
    }

    // Default text variant (matching ReportInaccuraciesButton text variant)
    return {
      ...baseStyle,
      padding: '8px 16px',
      color: hoveredLink ? '#60a5fa' : '#6b7280',
      fontSize: '14px',
      gap: '8px'
    };
  };

  if (!videoId) {
    console.error('Invalid YouTube URL provided:', videoUrl);
    return null;
  }

  return (
    <>
      {/* Video Link/Button */}
      <div style={{ position: 'relative', display: 'inline-block' }} className={className}>
        <button
          onClick={() => setShowVideoModal(true)}
          style={getLinkStyle()}
          onMouseEnter={() => setHoveredLink(true)}
          onMouseLeave={() => setHoveredLink(false)}
          title={linkText}
        >
          <FontAwesomeIcon icon={faCirclePlay} />
          <span>{linkText}</span>
        </button>
      </div>

      {/* Video Modal - rendered at document root using portal */}
      {showVideoModal && createPortal(
        <div 
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.85)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: 20
          }} 
          onClick={() => setShowVideoModal(false)}
        >
          <div 
            style={{
              background: 'linear-gradient(135deg, rgba(17, 24, 39, 0.98), rgba(31, 41, 55, 0.98))',
              borderRadius: 16,
              border: '1px solid rgba(59, 130, 246, 0.3)',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.6)',
              maxWidth: 1200,
              width: '100%',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }} 
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid rgba(59, 130, 246, 0.2)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'rgba(0, 0, 0, 0.3)'
            }}>
              <h3 style={{ 
                margin: 0, 
                color: '#f0f4ff', 
                fontSize: 18, 
                fontWeight: 600, 
                display: 'flex', 
                alignItems: 'center', 
                gap: 10 
              }}>
                <FontAwesomeIcon icon={faVideo} />
                <span>{linkText}</span>
              </h3>
              <button
                onClick={() => setShowVideoModal(false)}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                  background: 'rgba(30, 32, 42, 0.6)',
                  color: '#93c5fd',
                  fontSize: 20,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 200ms'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(59, 130, 246, 0.2)';
                  e.currentTarget.style.color = '#60a5fa';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(30, 32, 42, 0.6)';
                  e.currentTarget.style.color = '#93c5fd';
                }}
              >
                ×
              </button>
            </div>

            {/* Video Container */}
            <div style={{ 
              padding: '20px',
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#000'
            }}>
              <div style={{
                position: 'relative',
                width: '100%',
                paddingBottom: '56.25%', // 16:9 aspect ratio
                height: 0,
                overflow: 'hidden'
              }}>
                <iframe
                  src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
                  title="YouTube video player"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    borderRadius: '8px'
                  }}
                />
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default YouTubeVideoPopup;

